import { useEffect, useRef } from 'react';

const PATH_COUNT = 25;
const MAX_STEPS = 220;
const STEP_SIZE = 4;
const DRIFT = 0.5;
const ACCENT = '#B55C4A';
const BASE_COLORS = ['#444444', '#555555', '#4a4a4a', '#666666'];
const PRE_SIM_STEPS = 80;

function randomEdgePoint(w, h) {
  const side = Math.floor(Math.random() * 4);
  switch (side) {
    case 0: return { x: Math.random() * w, y: 0 };
    case 1: return { x: w, y: Math.random() * h };
    case 2: return { x: Math.random() * w, y: h };
    default: return { x: 0, y: Math.random() * h };
  }
}

function createPath(w, h) {
  const start = randomEdgePoint(w, h);
  const isAccent = Math.random() < 0.18;
  const color = isAccent ? ACCENT : BASE_COLORS[Math.floor(Math.random() * BASE_COLORS.length)];
  const opacity = isAccent ? 0.3 + Math.random() * 0.2 : 0.5 + Math.random() * 0.35;
  return {
    x: start.x,
    y: start.y,
    prevX: null,
    prevY: null,
    dx: (Math.random() - 0.5) * DRIFT * 2,
    dy: (Math.random() - 0.5) * DRIFT * 2,
    step: 0,
    maxSteps: MAX_STEPS + Math.floor(Math.random() * 80),
    color,
    opacity,
    width: 1 + Math.random() * 1.5,
  };
}

function stepPath(path) {
  path.prevX = path.x;
  path.prevY = path.y;
  path.dx += (Math.random() - 0.5) * 0.4;
  path.dy += (Math.random() - 0.5) * 0.4;
  path.dx *= 0.98;
  path.dy *= 0.98;
  path.x += path.dx * STEP_SIZE + (Math.random() - 0.5) * STEP_SIZE;
  path.y += path.dy * STEP_SIZE + (Math.random() - 0.5) * STEP_SIZE;
  path.step++;
}

function drawSegment(ctx, path) {
  if (path.prevX == null) return;
  const progress = path.step / path.maxSteps;
  const fadeIn = Math.min(progress * 5, 1);
  const fadeOut = progress > 0.7 ? 1 - (progress - 0.7) / 0.3 : 1;
  const alpha = path.opacity * fadeIn * fadeOut;
  if (alpha <= 0) return;

  ctx.beginPath();
  ctx.moveTo(path.prevX, path.prevY);
  ctx.lineTo(path.x, path.y);
  ctx.strokeStyle = path.color;
  ctx.globalAlpha = alpha;
  ctx.lineWidth = path.width;
  ctx.stroke();
  ctx.globalAlpha = 1;
}

export default function StochasticCanvas() {
  const canvasRef = useRef(null);
  const pathsRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    let dpr = window.devicePixelRatio || 1;
    let cw = window.innerWidth;
    let ch = window.innerHeight;

    function resize() {
      dpr = window.devicePixelRatio || 1;
      cw = window.innerWidth;
      ch = window.innerHeight;
      canvas.width = cw * dpr;
      canvas.height = ch * dpr;
      canvas.style.width = cw + 'px';
      canvas.style.height = ch + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener('resize', resize);

    // Initialize paths with staggered starting points
    pathsRef.current = [];
    for (let i = 0; i < PATH_COUNT; i++) {
      const p = createPath(cw, ch);
      // Stagger: each path starts at a random point in its lifecycle
      p.step = Math.floor(Math.random() * (p.maxSteps * 0.6));
      pathsRef.current.push(p);
    }

    // Pre-simulate: run steps synchronously so canvas is populated on first paint
    for (let s = 0; s < PRE_SIM_STEPS; s++) {
      for (let i = 0; i < pathsRef.current.length; i++) {
        const p = pathsRef.current[i];
        if (p.step < p.maxSteps) {
          stepPath(p);
          drawSegment(ctx, p);
        } else {
          pathsRef.current[i] = createPath(cw, ch);
        }
      }
    }

    function frame() {
      // Slow fade: darken existing content slightly each frame
      ctx.fillStyle = 'rgba(15, 15, 15, 0.015)';
      ctx.fillRect(0, 0, cw, ch);

      for (let i = 0; i < pathsRef.current.length; i++) {
        const p = pathsRef.current[i];
        if (p.step < p.maxSteps) {
          stepPath(p);
          drawSegment(ctx, p);
        } else {
          pathsRef.current[i] = createPath(cw, ch);
        }
      }

      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
